/**
 * Composite Data Provider
 *
 * Try WordPress first; on failure or empty result, fall back to Supabase.
 * Every call is logged to the in-memory query log.
 */

import type {
  DataProvider,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  CreateParams,
  CreateResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
  DeleteManyParams,
  DeleteManyResult,
  UpdateManyParams,
  GetOptionResult,
  SetOptionResult,
  QueryLogEntry,
} from './types';
import { wpDataProvider, WpOptionNotFoundError } from './wpDataProvider';
import { supabaseDataProvider } from './supabaseDataProvider';
import { addLogEntry, uid } from './queryLog';
import { WP_ENABLED } from './config';

// ── Helpers ──────────────────────────────────────────────────────────

const now = () => new Date().toISOString();

const logAndReturn = <T>(entry: Omit<QueryLogEntry, 'id' | 'timestamp'>): T extends void ? void : void => {
  addLogEntry({ id: uid(), timestamp: now(), ...entry });
  return undefined as any;
};

/**
 * Determine whether a result is "empty" and we should try the fallback.
 */
const isEmpty = (result: unknown): boolean => {
  if (result === null || result === undefined) return true;
  if (typeof result === 'object' && result !== null) {
    const r = result as Record<string, unknown>;
    if ('data' in r) {
      const d = r.data;
      if (d === null || d === undefined) return true;
      if (Array.isArray(d) && d.length === 0) return true;
      if (typeof d === 'object' && d !== null && Object.keys(d).length === 0) return true;
    }
  }
  return false;
};

type ProviderMethod = keyof DataProvider;

/**
 * Generic wrapper: try WP → fallback to Supabase on error or empty result.
 */
async function tryWithFallback<T>(
  method: ProviderMethod,
  resource: string,
  fn: (provider: DataProvider) => Promise<T>,
  params?: unknown,
): Promise<T> {
  // ── Try WordPress ──
  if (WP_ENABLED) {
    const start = performance.now();
    try {
      const result = await fn(wpDataProvider);
      const duration = Math.round(performance.now() - start);

      if (isEmpty(result)) {
        // WP returned empty — fall through to Supabase
        logAndReturn({
          source: 'wp',
          status: 'fallback',
          method,
          resource,
          params,
          durationMs: duration,
          error: 'Empty result – falling back to Supabase',
          fallbackUsed: true,
        });
      } else {
        logAndReturn({
          source: 'wp',
          status: 'success',
          method,
          resource,
          params,
          durationMs: duration,
          fallbackUsed: false,
        });
        return result;
      }
    } catch (err) {
      const duration = Math.round(performance.now() - start);

      // ── Option not found in WP → seed from Supabase then retry ──
      if (err instanceof WpOptionNotFoundError && WP_ENABLED) {
        logAndReturn({
          source: 'wp',
          status: 'fallback',
          method,
          resource,
          params,
          durationMs: duration,
          error: `${err.message} – seeding from Supabase`,
          fallbackUsed: true,
        });

        // Fetch the data from Supabase
        try {
          const sbResult = await fn(supabaseDataProvider);
          // Seed it into WP via setOption
          const seedData =
            sbResult && typeof sbResult === 'object' && 'data' in (sbResult as Record<string, unknown>)
              ? (sbResult as Record<string, unknown>).data
              : sbResult;

          if (seedData !== null && seedData !== undefined) {
            await wpDataProvider.setOption(resource, seedData);
            logAndReturn({
              source: 'wp',
              status: 'success',
              method: 'migrate' as ProviderMethod,
              resource,
              params: { seeded: true },
              durationMs: 0,
              fallbackUsed: true,
            });
          }
          return sbResult;
        } catch (seedErr) {
          console.warn(`[PMS Seed] Failed to seed option '${resource}' to WP:`, seedErr);
        }
      }

      logAndReturn({
        source: 'wp',
        status: 'error',
        method,
        resource,
        params,
        durationMs: duration,
        error: err instanceof Error ? err.message : String(err),
        fallbackUsed: true,
      });
    }
  }

  // ── Fallback: Supabase ──
  const start2 = performance.now();
  try {
    const result = await fn(supabaseDataProvider);
    const duration = Math.round(performance.now() - start2);
    logAndReturn({
      source: 'supabase',
      status: 'success',
      method,
      resource,
      params,
      durationMs: duration,
      fallbackUsed: WP_ENABLED,
    });
    return result;
  } catch (err) {
    const duration = Math.round(performance.now() - start2);
    logAndReturn({
      source: 'supabase',
      status: 'error',
      method,
      resource,
      params,
      durationMs: duration,
      error: err instanceof Error ? err.message : String(err),
      fallbackUsed: WP_ENABLED,
    });
    throw err;
  }
}

// ── Migration Helper ─────────────────────────────────────────────────

/**
 * Push Supabase records to WP so future calls return data from the primary source.
 * Each record is created individually; failures are logged but don't throw.
 */
async function migrateToWp(resource: string, records: Record<string, unknown>[]): Promise<number> {
  let migrated = 0;
  for (const record of records) {
    try {
      const { id, created_at, updated_at, user_id, ...data } = record;
      await wpDataProvider.create(resource, { data });
      migrated++;
    } catch (err) {
      console.warn(`[PMS Migrate] Failed to push ${resource} record to WP:`, err);
    }
  }
  console.log(`[PMS Migrate] ${resource}: ${migrated}/${records.length} records synced to WP`);
  return migrated;
}

// ── Composite Data Provider ──────────────────────────────────────────

export const compositeDataProvider: DataProvider = {
  async getList<T = Record<string, unknown>>(resource: string, params?: GetListParams): Promise<GetListResult<T>> {
    const result = await tryWithFallback<GetListResult<T>>('getList', resource, (p) => p.getList<T>(resource, params), params);

    // Auto-migrate: if WP was enabled but returned empty, and Supabase had data, push to WP
    if (WP_ENABLED && result.data.length > 0) {
      // Check if WP is actually empty for this resource
      try {
        const wpResult = await wpDataProvider.getList(resource);
        if (isEmpty(wpResult)) {
          const migrated = await migrateToWp(resource, result.data as Record<string, unknown>[]);
          logAndReturn({
            source: 'wp',
            status: 'success',
            method: 'migrate',
            resource,
            params: { count: migrated },
            durationMs: 0,
            fallbackUsed: false,
          });
        }
      } catch {
        // WP unreachable — skip migration silently
      }
    }

    return result;
  },

  getOne<T = Record<string, unknown>>(resource: string, params: GetOneParams): Promise<GetOneResult<T>> {
    return tryWithFallback('getOne', resource, (p) => p.getOne<T>(resource, params), params);
  },

  create<T = Record<string, unknown>>(resource: string, params: CreateParams): Promise<CreateResult<T>> {
    return tryWithFallback('create', resource, (p) => p.create<T>(resource, params), params);
  },

  update<T = Record<string, unknown>>(resource: string, params: UpdateParams): Promise<UpdateResult<T>> {
    return tryWithFallback('update', resource, (p) => p.update<T>(resource, params), params);
  },

  delete<T = Record<string, unknown>>(resource: string, params: DeleteParams): Promise<DeleteResult<T>> {
    return tryWithFallback('delete', resource, (p) => p.delete<T>(resource, params), params);
  },

  deleteMany(resource: string, params: DeleteManyParams): Promise<DeleteManyResult> {
    return tryWithFallback('deleteMany', resource, (p) => p.deleteMany(resource, params), params);
  },

  updateMany<T = Record<string, unknown>>(resource: string, params: UpdateManyParams<T>): Promise<{ updated: number }> {
    return tryWithFallback('updateMany', resource, (p) => p.updateMany<T>(resource, params), params);
  },

  getOption<T = unknown>(name: string): Promise<GetOptionResult<T>> {
    return tryWithFallback('getOption', name, (p) => p.getOption<T>(name));
  },

  setOption<T = unknown>(name: string, value: T): Promise<SetOptionResult<T>> {
    return tryWithFallback('setOption', name, (p) => p.setOption<T>(name, value), value);
  },
};
